import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ClientsApiActions } from '../../@store/clients/clients.actions';
import {
  selectClients,
  selectClientsError,
  selectClientsLoading,
  selectClientsTotalCount,
} from '../../@store/clients/clients.selectors';
import { AllClientsTable, Client } from '../../@store/clients/clients.state.interface';
import { selectIsOrdersAdmin, selectIsOrdersManager } from '../../@store/user/user.selectors';
import { CONFIG } from '../../config';
import { SafeAny } from '../../types/safe-any';
import { PaginationComponent } from '../@shared/pagination/pagination.component';

@Component({
  selector: 'ciwc-clients',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    PaginationComponent,
    MatTooltipModule,
    MatProgressSpinnerModule,
    RouterModule,
  ],
  templateUrl: './clients.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientsComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  readonly clients = this.store.selectSignal(selectClients);
  readonly clientsLength = this.store.selectSignal(selectClientsTotalCount);
  readonly clientsLoading = this.store.selectSignal(selectClientsLoading);
  readonly clientsError = this.store.selectSignal(selectClientsError);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly isOrdersAdmin = this.store.selectSignal(selectIsOrdersAdmin);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<AllClientsTable>;

  dataSource: MatTableDataSource<AllClientsTable> = new MatTableDataSource<AllClientsTable>();
  displayedColumns: string[] = ['company', 'firstName', 'email', 'phone', 'actions'];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  totalClients = 0;
  pageIndex = 0;
  currentSort: Sort = { active: 'company', direction: 'asc' };
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  searchTerm = '';

  searchControl = new FormControl('');

  constructor() {
    effect(() => {
      this.initValues();
    });
  }

  ngOnInit(): void {
    this.loadClients();
    this.listenToSearch();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    this.store.dispatch(
      ClientsApiActions.loadClients({
        payload: {
          sort: formattedSort,
          offset: this.pageIndex + 1,
          limit: this.pageSize,
          q: this.searchTerm,
        },
      }),
    );
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    const formattedSort = sort.direction === 'desc' ? `-${sort.active}` : sort.active;

    this.store.dispatch(
      ClientsApiActions.loadClients({
        payload: {
          sort: formattedSort,
          offset: this.pageIndex + 1,
          limit: this.pageSize,
          q: this.searchTerm,
        },
      }),
    );
  }

  editClient(client: SafeAny): void {
    this.router.navigate(['/clients/edit', client.id]);
  }

  private initValues(): void {
    this.dataSource.data =
      this.clients()?.map((client: Client) => ({
        id: client.id,
        company: client.company,
        contact: `${client.firstName} ${client.lastName}`,
        email: client.email,
        phone: client.primaryPhone,
      })) ?? [];

    this.totalClients = this.clientsLength();
  }

  private listenToSearch(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.searchTerm = value ?? '';
        this.pageIndex = 0;
        this.pageSize = CONFIG.PAGE_SIZE;
        this.currentSort = { active: 'company', direction: 'asc' };

        this.loadClients();
      });
  }

  private loadClients(): void {
    this.store.dispatch(
      ClientsApiActions.loadClients({
        payload: {
          sort: 'company',
          offset: 1,
          limit: 10,
          q: this.searchTerm,
        },
      }),
    );
  }
}
